import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, createConnectQueryKey, createQueryOptions, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import * as Sentry from '@sentry/react'
import { Code, ConnectError, type Transport } from '@connectrpc/connect'
import {
  getOrganizationBillingState,
  listPlanOptions,
  previewPlanChange,
  changeSubscriptionPlan,
  cancelSubscription,
  resumeSubscription,
  createCheckoutSession,
  reconcileCheckoutSession,
  getOrganizationSubscription,
  getPaymentHistory,
  getCustomerPortalURL,
  getSeatUsage,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetOrganizationBillingStateRequestSchema,
  ListPlanOptionsRequestSchema,
  PreviewPlanChangeRequestSchema,
  ChangeSubscriptionPlanRequestSchema,
  CancelSubscriptionRequestSchema,
  ResumeSubscriptionRequestSchema,
  CreateCheckoutSessionRequestSchema,
  ReconcileCheckoutSessionRequestSchema,
  GetOrganizationSubscriptionRequestSchema,
  GetPaymentHistoryRequestSchema,
  GetCustomerPortalURLRequestSchema,
  GetSeatUsageRequestSchema,
  BillingAccessStateEnum,
  type PlanKeyEnum,
  type BillingIntervalEnum,
} from '@/gen/cortado/v1/subscriptions_pb'
import { authStore } from './auth.store'
import { connectTransport } from '../lib/connect-transport'
import {
  isLiveCheckoutState,
  pollBillingCheckout,
  type CheckoutPollMode,
  type CheckoutPollOutcome,
} from './billing-checkout-poller'

export type { CheckoutPollMode, CheckoutPollOutcome } from './billing-checkout-poller'

/**
 * Query options for the GetOrganizationBillingState RPC, for use in route
 * guards via queryClient.ensureQueryData (see the F7/F8 hard-gate guard in
 * routes/_app.tsx). Uses the same connectTransport singleton as
 * useBillingState so the cache key matches.
 */
export function getOrganizationBillingStateQueryOptions(
  organizationId: string,
  transport: Transport = connectTransport
) {
  return createQueryOptions(
    getOrganizationBillingState,
    create(GetOrganizationBillingStateRequestSchema, { organizationId }),
    { transport }
  )
}

// Stripe substitutes {CHECKOUT_SESSION_ID} before navigating back to the app.
// The /company route
// parses and forwards checkout_id so BillingSection can resume the
// invalidate+poll flow if a redirect-based payment method bounces the whole
// page instead of confirming in place (F10).
export function buildBillingCheckoutSuccessUrl(returnPath = '/company?section=subscription'): string {
  const separator = returnPath.includes('?') ? '&' : '?'
  return `${window.location.origin}${returnPath}${separator}checkout_id={CHECKOUT_SESSION_ID}`
}

const PENDING_CHECKOUT_KEY_PREFIX = 'cortado_pending_checkout:'

export function persistPendingCheckoutId(organizationId: string, checkoutId: string): void {
  try {
    localStorage.setItem(`${PENDING_CHECKOUT_KEY_PREFIX}${organizationId}`, checkoutId)
  } catch {
    // Recovery still works for the current mounted checkout when storage is unavailable.
  }
}

export function readPendingCheckoutId(organizationId: string): string | null {
  try {
    return localStorage.getItem(`${PENDING_CHECKOUT_KEY_PREFIX}${organizationId}`)
  } catch {
    return null
  }
}

export function clearPendingCheckoutId(organizationId: string): void {
  try {
    localStorage.removeItem(`${PENDING_CHECKOUT_KEY_PREFIX}${organizationId}`)
  } catch {
    // Nothing else to do.
  }
}

/**
 * Hook to fetch the org billing/access state - the single source every
 * billing gate and banner in the app reads.
 */
export function useBillingState(organizationId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    getOrganizationBillingState,
    create(GetOrganizationBillingStateRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && !!organizationId,
    }
  )

  return {
    billingState: query.data?.billingState ?? null,
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to list the purchasable plan+interval combinations for an
 * organization. Only the assigned plan's 3 intervals come back when
 * plan_is_assigned is true on OrganizationBillingState (R-71).
 */
export function useListPlanOptions(organizationId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    listPlanOptions,
    create(ListPlanOptionsRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && !!organizationId,
    }
  )

  return {
    planOptions: query.data?.planOptions ?? [],
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
  }
}

/**
 * Hook to fetch an organization's seat usage/billing breakdown (F12,
 * R-85..R-92): included seats, active users, billable seats, any pending
 * reduction and the per-user roster. Cortado staff are excluded from
 * billableSeats server-side (R-109) but still appear in seatUsers with
 * isBillable=false so the UI can show who's on the org without billing them.
 */
export function useSeatUsage(organizationId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    getSeatUsage,
    create(GetSeatUsageRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && !!organizationId,
    }
  )

  return {
    includedSeats: query.data?.includedSeats ?? 0,
    activeUsers: query.data?.activeUsers ?? 0,
    billableSeats: query.data?.billableSeats ?? 0,
    pendingReductionTo: query.data?.pendingReductionTo ?? 0,
    seatPriceCents: query.data?.seatPriceCents ?? 0n,
    nextChargeEstimateCents: query.data?.nextChargeEstimateCents ?? 0n,
    seatUsers: query.data?.seatUsers ?? [],
    eventStaffUsers: query.data?.eventStaffUsers ?? 0,
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch the subscription status for an organization.
 */
export function useOrganizationSubscription(organizationId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    getOrganizationSubscription,
    create(GetOrganizationSubscriptionRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && !!organizationId,
    }
  )

  return {
    hasSubscription: query.data?.hasSubscription ?? false,
    subscription: query.data?.subscription ?? null,
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch payment history for an organization.
 */
export function usePaymentHistory(organizationId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    getPaymentHistory,
    create(GetPaymentHistoryRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && !!organizationId,
    }
  )

  return {
    payments: query.data?.payments || [],
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to get the customer portal URL and redirect.
 */
export function useCustomerPortalURL() {
  const mutation = useMutation(getCustomerPortalURL, {
    onSuccess: (response) => {
      if (response.portalUrl) {
        window.open(response.portalUrl, '_blank')
      }
    },
  })

  return {
    openPortal: (organizationId: string) => {
      return mutation.mutateAsync(
        create(GetCustomerPortalURLRequestSchema, { organizationId })
      )
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

function useInvalidateBillingQueries() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  return useCallback(
    (organizationId: string) => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getOrganizationBillingState,
          transport,
          input: { organizationId },
          cardinality: 'finite',
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listPlanOptions,
          transport,
          input: { organizationId },
          cardinality: 'finite',
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getOrganizationSubscription,
          transport,
          input: { organizationId },
          cardinality: 'finite',
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getPaymentHistory,
          transport,
          input: { organizationId },
          cardinality: 'finite',
        }),
      })
    },
    [queryClient, transport]
  )
}

/**
 * Hook to preview a plan/interval change before committing it (R-35, R-36).
 */
export function usePreviewPlanChange() {
  const mutation = useMutation(previewPlanChange)

  return {
    previewAsync: (input: {
      organizationId: string
      planKey: PlanKeyEnum
      billingInterval: BillingIntervalEnum
    }) => mutation.mutateAsync(create(PreviewPlanChangeRequestSchema, input)),
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  }
}

/**
 * Hook to change an organization's plan and/or billing interval (R-33..R-36).
 */
export function useChangeSubscriptionPlan() {
  const invalidate = useInvalidateBillingQueries()
  const mutation = useMutation(changeSubscriptionPlan)

  return {
    changeAsync: async (input: {
      organizationId: string
      planKey: PlanKeyEnum
      billingInterval: BillingIntervalEnum
    }) => {
      const response = await mutation.mutateAsync(create(ChangeSubscriptionPlanRequestSchema, input))
      invalidate(input.organizationId)
      return response
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to schedule cancellation at the end of the current paid period
 * (R-53). Always cancel_at_period_end: true - immediate closure is
 * admin-only.
 */
export function useCancelSubscription() {
  const invalidate = useInvalidateBillingQueries()
  const mutation = useMutation(cancelSubscription)

  return {
    cancelAsync: async (input: {
      organizationId: string
      cancellationReason: string
      comment?: string
    }) => {
      const response = await mutation.mutateAsync(create(CancelSubscriptionRequestSchema, input))
      invalidate(input.organizationId)
      return response
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to reverse a scheduled cancellation before the paid term expires
 * (R-75).
 */
export function useResumeSubscription() {
  const invalidate = useInvalidateBillingQueries()
  const mutation = useMutation(resumeSubscription)

  return {
    resumeAsync: async (organizationId: string) => {
      const response = await mutation.mutateAsync(create(ResumeSubscriptionRequestSchema, { organizationId }))
      invalidate(organizationId)
      return response
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to create a Stripe Checkout Session for the custom payment UI. The
 * generated response exposes Stripe's `client_secret` as `clientSecret`;
 * callers pass that value to CheckoutProvider without navigating to
 * checkout_url.
 */
export function useCreateCheckoutSession() {
  const { mutateAsync, isPending, isError, error } = useMutation(createCheckoutSession)

  const createAsync = useCallback(
    (input: {
      organizationId: string
      planKey: PlanKeyEnum
      billingInterval: BillingIntervalEnum
      returnPath?: string
    }) => {
      const { returnPath, ...requestInput } = input
      return mutateAsync(
        create(CreateCheckoutSessionRequestSchema, {
          ...requestInput,
          successUrl: buildBillingCheckoutSuccessUrl(returnPath),
          embedOrigin: window.location.origin,
        })
      )
    },
    [mutateAsync]
  )

  return {
    createAsync,
    isLoading: isPending,
    isError,
    error,
  }
}

/**
 * Shared post-checkout polling behavior (F3, F10). The Stripe webhook that
 * advances org_billing_state is asynchronous, so after a checkout completes
 * (either an in-page confirm, or a redirect-based
 * fallback landing back on /company with a checkout_id) we poll
 * GetOrganizationBillingState every 2s, up to 10 attempts, until the state
 * advances. After 30 seconds we request one authoritative provider
 * reconciliation, then poll for another 30 seconds. On exhaustion we fire a Sentry event tagged with the
 * organization and checkout so DEC-52's stuck-payer message can be shown
 * instead of spinning forever.
 */
export function useBillingCheckoutSuccessPoll(organizationId: string) {
  const { billingState, refetch } = useBillingState(organizationId)
  const invalidate = useInvalidateBillingQueries()
  const { mutateAsync: reconcileAsync } = useMutation(reconcileCheckoutSession)
  const [outcome, setOutcome] = useState<CheckoutPollOutcome>('idle')
  const beforeStateRef = useRef<BillingAccessStateEnum | undefined>(undefined)
  const activePollRef = useRef<AbortController | null>(null)
  const activeOrganizationIdRef = useRef<string | null>(null)

  useEffect(() => {
    activeOrganizationIdRef.current = organizationId
    activePollRef.current?.abort()
    activePollRef.current = null
    setOutcome('idle')

    return () => {
      activeOrganizationIdRef.current = null
      activePollRef.current?.abort()
      activePollRef.current = null
    }
  }, [organizationId])

  // Track the last known state whenever we are NOT mid-poll, so
  // startPolling always compares against the state that was current right
  // before checkout completed - not a value captured once at mount time.
  useEffect(() => {
    if (activePollRef.current === null) {
      beforeStateRef.current = billingState?.state
    }
  }, [organizationId, billingState?.state])

  const startPolling = useCallback(
    (checkoutId: string, mode: CheckoutPollMode = 'embedded', reconcileImmediately = false) => {
      if (
        !organizationId ||
        activeOrganizationIdRef.current !== organizationId ||
        activePollRef.current !== null
      ) {
        return false
      }

      const controller = new AbortController()
      activePollRef.current = controller
      setOutcome('polling')
      persistPendingCheckoutId(organizationId, checkoutId)
      invalidate(organizationId)

      const finish = (terminalOutcome: Extract<CheckoutPollOutcome, 'succeeded' | 'stuck'>) => {
        if (activePollRef.current !== controller || controller.signal.aborted) return
        activePollRef.current = null
        setOutcome(terminalOutcome)

        if (terminalOutcome === 'succeeded') {
          clearPendingCheckoutId(organizationId)
          // The transition response only refreshes the billing-state query.
          // Reconcile every billing consumer after the webhook has advanced
          // so plan cards, legacy subscription details, and receipts cannot
          // remain stale behind the newly-live access state.
          invalidate(organizationId)
        } else {
          Sentry.captureMessage('billing.checkout_stuck', {
            level: 'warning',
            tags: { organization_id: organizationId, checkout_id: checkoutId },
          })
        }
      }

      // A redirect return can mount only after the webhook has won the race
      // with the page load. In that case the currently loaded ACTIVE/TRIALING
      // (or other live) baseline is itself authoritative success; waiting for
      // a transition that cannot occur would incorrectly report a stuck payer.
      if (
        mode === 'redirect' &&
        beforeStateRef.current !== undefined &&
        isLiveCheckoutState(beforeStateRef.current)
      ) {
        finish('succeeded')
        return true
      }

      void pollBillingCheckout({
        mode,
        beforeState: beforeStateRef.current,
        signal: controller.signal,
        refetchState: async () => {
          const result = await refetch()
          return result.data?.billingState?.state
        },
        reconcile: async () => {
          await reconcileAsync(
            create(ReconcileCheckoutSessionRequestSchema, { organizationId, checkoutId })
          )
        },
        invalidate: () => invalidate(organizationId),
        reconcileImmediately,
        onReconcileError: (error) => {
          Sentry.captureException(error, {
            tags: {
              organization_id: organizationId,
              checkout_id: checkoutId,
              ...(error instanceof ConnectError ? { code: Code[error.code] } : {}),
            },
          })
        },
      }).then((result) => {
        if (activePollRef.current !== controller || controller.signal.aborted) return
        beforeStateRef.current = result.baseline
        if (result.outcome === 'succeeded' || result.outcome === 'stuck') {
          finish(result.outcome)
        }
      })

      return true
    },
    [organizationId, invalidate, reconcileAsync, refetch]
  )

  return { outcome, startPolling }
}
